/**
 * Mimic Design System
 *
 * Usage:
 *   import { tokens, Table, Th, Td, LoaderBar, ... } from '../styles/ds'
 *
 * To use in another project: copy this file + install styled-components.
 */

import styled, { keyframes } from 'styled-components'

// ─── Tokens ──────────────────────────────────────────────────────────────────

export const tokens = {
  color: {
    // Backgrounds
    bg:       '#0a0a0a',
    surface:  '#111',
    elevated: '#161616',
    input:    '#0d0d0d',

    // Borders
    border:      'rgba(255,255,255,0.07)',
    borderLight: 'rgba(255,255,255,0.05)',
    borderHover: 'rgba(255,255,255,0.14)',

    // Text
    textPrimary:   '#ededed',
    textSecondary: '#a0a0a0',
    textMuted:     '#777',
    textFaint:     '#555',

    // Accent (purple)
    accent:      '#6f5ce6',
    accentLight: '#a89aff',
    accentBg:    'rgba(111,92,230,0.1)',
    accentBgHover: 'rgba(111,92,230,0.2)',
    accentBorder: 'rgba(111,92,230,0.3)',

    // Semantic
    success:       '#33C2B0',
    successBg:     'rgba(51,194,176,0.08)',
    successBorder: 'rgba(51,194,176,0.25)',
    error:         '#ff4444',
    errorBg:       'rgba(255,68,68,0.08)',
    errorBorder:   'rgba(255,68,68,0.25)',
    warning:       '#FFCC33',
    warningBg:     'rgba(255,204,51,0.08)',
    warningBorder: 'rgba(255,204,51,0.2)',
  },
  font: {
    sans: `-apple-system, BlinkMacSystemFont, 'Inter', 'DMSans', sans-serif`,
    mono: `'SF Mono', 'Fira Code', monospace`,
  },
  radius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
  },
}

// ─── Animations ──────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const dot = keyframes`
  0%, 80%, 100% { opacity: 0; }
  40%           { opacity: 1; }
`

// ─── Layout ──────────────────────────────────────────────────────────────────

export const Page = styled.div`
  font-family: ${tokens.font.sans};
`

/** Full-screen centered page with dot-grid background — used for landing/auth screens */
export const LandingRoot = styled.div`
  min-height: 100vh;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${tokens.font.sans};
  position: relative;
  overflow: hidden;
  background-image: radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 28px 28px;
`

/** Radial purple glow — place as first child inside LandingRoot */
export const Glow = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(111,92,230,0.18) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  filter: blur(40px);
`

/** Glass card — for modals, auth forms, floating panels */
export const GlassCard = styled.div`
  position: relative;
  z-index: 1;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(111,92,230,0.06), 0 24px 64px rgba(0,0,0,0.6);
  animation: ${fadeIn} 0.4s ease;
`

export const SectionLabel = styled.div`
  color: ${tokens.color.textFaint};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 12px;
`

export const PageTitle = styled.h1`
  color: ${tokens.color.textPrimary};
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
`

// ─── Table ───────────────────────────────────────────────────────────────────

export const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.lg};
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

export const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: ${tokens.color.textMuted};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`

export const Td = styled.td`
  padding: 10px 12px;
  color: ${tokens.color.textSecondary};
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid ${tokens.color.borderLight};

  tr:last-child & { border-bottom: none; }
`

export const AccentTd = styled(Td)`
  color: ${tokens.color.textPrimary};
  font-weight: 500;
`

// ─── Loader ──────────────────────────────────────────────────────────────────

export const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

export const Skeleton = styled.div<{ width?: number; height?: number }>`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  height: ${({ height }) => (height ? `${height}px` : '16px')};
  border-radius: ${tokens.radius.sm};
  background: linear-gradient(90deg, #1a1a1a 25%, #222 50%, #1a1a1a 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`

// ─── Buttons ─────────────────────────────────────────────────────────────────

export const ActionBtn = styled.button`
  height: 26px;
  padding: 0 9px;
  background: rgba(255,255,255,0.04);
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.sm};
  color: ${tokens.color.textSecondary};
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
`

export const EditBtn = styled(ActionBtn)`
  &:hover { border-color: rgba(111,92,230,0.4); color: ${tokens.color.accentLight}; }
`

export const SaveBtn = styled(ActionBtn)`
  &:hover { border-color: rgba(51,194,176,0.4); color: ${tokens.color.success}; }
`

export const DeleteBtn = styled(ActionBtn)`
  &:hover { border-color: rgba(255,68,68,0.4); color: ${tokens.color.error}; }
`

export const GhostBtn = styled(ActionBtn)``

export const AddBtn = styled.button`
  height: 28px;
  padding: 0 12px;
  background: ${tokens.color.accentBg};
  border: 1px solid ${tokens.color.accentBorder};
  border-radius: ${tokens.radius.md};
  color: ${tokens.color.accentLight};
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${tokens.color.accentBgHover};
    border-color: rgba(111,92,230,0.5);
  }
`

export const SubmitBtn = styled.button`
  height: 34px;
  padding: 0 16px;
  background: ${tokens.color.accentBg};
  border: 1px solid rgba(111,92,230,0.35);
  border-radius: ${tokens.radius.md};
  color: ${tokens.color.accentLight};
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${tokens.color.accentBgHover};
    border-color: rgba(111,92,230,0.5);
  }
`

export const PrimaryBtn = styled.button`
  height: 32px;
  padding: 0 14px;
  background: ${tokens.color.accent};
  border: none;
  border-radius: ${tokens.radius.md};
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #5a4bd4; }
`

// ─── Status ──────────────────────────────────────────────────────────────────

export const StatusDot = styled.span<{ active: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ active }) => (active ? tokens.color.success : tokens.color.error)};
`

export const Badge = styled.span<{ variant?: 'success' | 'error' | 'warning' | 'accent' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: ${tokens.radius.sm};
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;

  ${({ variant = 'accent' }) => {
    switch (variant) {
      case 'success': return `background: ${tokens.color.successBg}; border: 1px solid ${tokens.color.successBorder}; color: ${tokens.color.success};`
      case 'error':   return `background: ${tokens.color.errorBg};   border: 1px solid ${tokens.color.errorBorder};   color: ${tokens.color.error};`
      case 'warning': return `background: ${tokens.color.warningBg}; border: 1px solid ${tokens.color.warningBorder}; color: ${tokens.color.warning};`
      default:        return `background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.25); color: ${tokens.color.accentLight};`
    }
  }}
`

// ─── Feedback ────────────────────────────────────────────────────────────────

export const FeedbackBanner = styled.div<{ error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${({ error }) => error ? tokens.color.errorBg : tokens.color.successBg};
  border: 1px solid ${({ error }) => error ? tokens.color.errorBorder : tokens.color.successBorder};
  border-radius: ${tokens.radius.md};
  color: ${({ error }) => error ? tokens.color.error : tokens.color.success};
  font-size: 13px;
`

// ─── Form ────────────────────────────────────────────────────────────────────

export const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 34px;
  padding: 0 10px;
  background: ${tokens.color.input};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.md};
  color: ${tokens.color.textPrimary};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: ${tokens.color.textFaint}; }
  &:focus { border-color: rgba(111,92,230,0.5); }
`

export const InlineInput = styled.input`
  background: ${tokens.color.input};
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: ${tokens.radius.sm};
  color: ${tokens.color.textPrimary};
  font-size: 12px;
  padding: 0 6px;
  height: 26px;
  width: 120px;
  outline: none;

  &:focus { border-color: rgba(111,92,230,0.5); }
`

// ─── Mono ─────────────────────────────────────────────────────────────────────

export const Mono = styled.span`
  font-family: ${tokens.font.mono};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`

// ─── Loading Dots ─────────────────────────────────────────────────────────────

const DotEl = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  animation: ${dot} 1.2s ease-in-out infinite;
`

export const DotsWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

/** Drop-in loading indicator for buttons: <LoadingDots /> */
export const LoadingDots = () => (
  <DotsWrap>
    <DotEl style={{ animationDelay: '0s' }} />
    <DotEl style={{ animationDelay: '0.15s' }} />
    <DotEl style={{ animationDelay: '0.3s' }} />
  </DotsWrap>
)
